import { type TypographyOptions } from '@mui/material/styles/createTypography'

import satoshiFontFamily from './fontFamily'

type ExtendedTypographyOptions = TypographyOptions & {
  h7: React.CSSProperties
}

/**
 * Satoshi Variable is used for all typography.
 * It's a variable font supporting weights 300-900.
 *
 * The scale is deliberately compact (14px body, Linear-style density) — the
 * root font-size is also reduced in GlobalStyles so rem-based spacing, input
 * chrome, and icons tighten proportionally with the type.
 */
export const fontFamily = satoshiFontFamily

const typography = {
  fontFamily,

  // Matches `html { fontSize: 87.5% }` in GlobalStyles (14px at the browser
  // default of 16px). `responsiveFontSizes()` converts variant sizes to rem
  // against this value; leaving the MUI default of 16 lets headings shrink an
  // extra 12.5% under the 14px root and lets custom `h7` (still px) outrank `h6`.
  htmlFontSize: 14,

  h1: {
    fontFamily,
    fontWeight: 600,
    fontSize: '56px',
    lineHeight: 64 / 56,
    letterSpacing: '-1.5px'
  },

  h2: {
    fontFamily,
    fontWeight: 300,
    fontSize: '44px',
    lineHeight: 1.2,
    letterSpacing: '-0.5px'
  },

  h3: {
    fontFamily,
    fontWeight: 600,
    fontSize: '34px',
    lineHeight: 42 / 34,
    letterSpacing: '-1px'
  },

  h4: {
    fontFamily,
    fontWeight: 400,
    fontSize: '25px',
    lineHeight: 33 / 25,
    letterSpacing: '0.25px'
  },

  h5: {
    fontFamily,
    fontWeight: 400,
    fontSize: '20px',
    lineHeight: 28 / 20,
    letterSpacing: 0
  },

  h6: {
    fontFamily,
    fontWeight: 600,
    fontSize: '17px',
    lineHeight: 26 / 17,
    letterSpacing: '0.15px'
  },

  h7: {
    fontFamily,
    fontWeight: 600,
    fontSize: '15px',
    lineHeight: 22 / 15,
    letterSpacing: '0.15px'
  },

  subtitle1: {
    fontFamily,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 24 / 14,
    letterSpacing: '0.15px'
  },

  subtitle2: {
    fontFamily,
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: 20 / 13,
    letterSpacing: '0.1px'
  },

  body1: {
    fontFamily,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.5,
    letterSpacing: '0.15px'
  },

  body2: {
    fontFamily,
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: 19 / 13,
    letterSpacing: '0.17px'
  },

  caption: {
    fontFamily,
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: 20 / 12,
    letterSpacing: '0.4px'
  },

  overline: {
    fontFamily,
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: 32 / 12,
    letterSpacing: '1px',
    textTransform: 'uppercase'
  },

  button: {
    letterSpacing: '0.4px'
  }
} satisfies ExtendedTypographyOptions

export default typography
